// Реализация локальной базы на SQLite в WebAssembly (sql.js). Одна и та же сборка работает в браузере
// и в Node, поэтому логика ядра проверяется тестами ровно в том виде, в каком поедет к пользователю.
//
// Десктопная реализация (нативный SQLite + SQLCipher) появится вместе со сборкой клиента: интерфейс
// `Database` для неё уже готов, а шифрование базы — вопрос адаптера, а не ядра.

import initSqlJs from "sql.js";
import type { Database as SqlJsHandle, SqlJsStatic } from "sql.js";

import type { Database, Row, SqlValue } from "./database.ts";

let runtime: Promise<SqlJsStatic> | undefined;

/** Загружает WASM один раз на процесс. `locateFile` нужен и браузеру, и Node. */
function sqlJs(locateFile?: (file: string) => string): Promise<SqlJsStatic> {
  runtime ??= initSqlJs(locateFile === undefined ? undefined : { locateFile });
  return runtime;
}

/** База поверх sql.js. */
export class SqlJsDatabase implements Database {
  readonly #db: SqlJsHandle;
  #depth = 0;

  private constructor(db: SqlJsHandle) {
    this.#db = db;
    // Внешние ключи в SQLite выключены по умолчанию — без них «участник без чата» проходит молча.
    this.#db.run("PRAGMA foreign_keys = ON");
  }

  /** Открывает базу: из сохранённых байтов или пустую. */
  static async open(options: { bytes?: Uint8Array; locateFile?: (file: string) => string } = {}): Promise<SqlJsDatabase> {
    const SQL = await sqlJs(options.locateFile);
    return new SqlJsDatabase(new SQL.Database(options.bytes));
  }

  run(sql: string, params: readonly SqlValue[] = []): void {
    // Без параметров — путь, который исполняет несколько операторов подряд (им приходит схема целиком).
    // С параметрами sql.js готовит запрос, а подготовленный запрос — всегда ровно один оператор.
    if (params.length === 0) {
      this.#db.run(sql);
      return;
    }
    this.#db.run(sql, params as SqlValue[]);
  }

  all(sql: string, params: readonly SqlValue[] = []): Row[] {
    const statement = this.#db.prepare(sql);
    try {
      statement.bind(params as SqlValue[]);
      const rows: Row[] = [];
      while (statement.step()) {
        rows.push(statement.getAsObject());
      }
      return rows;
    } finally {
      statement.free();
    }
  }

  get(sql: string, params: readonly SqlValue[] = []): Row | undefined {
    return this.all(sql, params)[0];
  }

  /**
   * Транзакция; вложенные вызовы разделяют внешнюю. Без этого нельзя объединить пачку изменений: внутри
   * уже есть операции со своими транзакциями (подмена черновика, очередь), а SQLite вложенный BEGIN
   * не принимает — падало бы всё применение событий целиком.
   */
  transaction<T>(body: () => T): T {
    if (this.#depth > 0) {
      this.#depth++;
      try {
        return body();
      } finally {
        this.#depth--;
      }
    }
    this.#db.run("BEGIN");
    this.#depth = 1;
    try {
      const result = body();
      this.#db.run("COMMIT");
      return result;
    } catch (err) {
      this.#db.run("ROLLBACK");
      throw err;
    } finally {
      this.#depth = 0;
    }
  }

  export(): Uint8Array {
    return this.#db.export();
  }

  close(): void {
    this.#db.close();
  }
}
